/* In-app wording for companies outside Saudi Arabia. The app's copy is written for Saudi Arabia (GOSI, Mudad, Qiwa,
 * Iqama, Saudization, SAR…); for a company in another GCC country `localText` swaps those terms for the country's own
 * (social insurance, WPS, ministry, ID card, localisation programme, currency) from contracts/countries.json.
 * useI18n() applies it to every t() / L() string, so pages stay written once. Saudi Arabia is returned unchanged. */
import type { Lang } from "../data/types";
import type { CountryInfo } from "../domain/country";

type Rule = [RegExp, string | ((...m: string[]) => string)];

const AR = "\\u0600-\\u06FF";
/** Whole Arabic word, keeping a one-letter prefix (و ب ل ف ك) in front of the replacement. */
const arWord = (w: string) => new RegExp(`(?<![${AR}])([وبلفك]?)${w}(?![${AR}])`, "g");
const en = (w: string) => new RegExp(`\\b${w}\\b`, "g");
/** "ICP / GDRFA (Emirates ID and residence)" → "ICP / GDRFA". */
const head = (s: string) => s.split(" (")[0].trim();

function enRules(c: CountryInfo): Rule[] {
  const ot = Math.round(c.overtime * 100) + "%";
  return [
    // StartHR's own hosting stays in Saudi Arabia for every company
    [/\bData hosted in KSA(?: · PDPL)?/g, c.hosting[0]],
    [/\b(?:the )?Kingdom of Saudi Arabia\b/g, c.name[0]],
    [en("Saudi Arabia"), c.name[0]],
    [en("KSA"), c.short[0]],
    [/\bSaudi Labou?r Law\b/g, c.laborLaw[0]],
    [/\bLabou?r Law Art\. \d+/g, c.laborLaw[0]],
    [/\bWPS · Mudad\b/g, c.wps.name[0]],
    [/\bMudad salary file\b/g, c.wps.file[0]],
    [en("Mudad"), "WPS"],
    [en("Qiwa"), c.ministry.short[0]],
    [en("MHRSD"), c.ministry.short[0]],
    [en("Muqeem"), head(c.permits.name[0])],
    [en("Nitaqat"), c.localisation.short[0]],
    [en("Saudization"), c.localisation.short[0]],
    [en("Non-Saudis"), "Expatriates"],
    [en("non-Saudis"), "expatriates"],
    [en("Non-Saudi"), "Expatriate"],
    [en("non-Saudi"), "expatriate"],
    [en("Saudis"), c.demonym[0] + "s"],
    [en("Saudi"), c.demonym[0]],
    [en("GOSI"), c.si.short[0]],
    [en("SANED"), c.si.parts.saned[0]],
    [en("Iqamas"), c.nationalId.resident[0] + "s"],
    [en("Iqama"), c.nationalId.resident[0]],
    [en("National ID"), c.nationalId.citizen[0]],
    [en("ZATCA"), c.vat.authority[0]],
    [en("SAR"), c.currency.code],
    [/\b150%/g, ot],
  ];
}

function arRules(c: CountryInfo): Rule[] {
  const ot = Math.round(c.overtime * 100) + "%";
  const dem = c.demonym[1];
  return [
    [/البيانات مستضافة في المملكة(?: · نظام حماية البيانات| · PDPL)?/g, c.hosting[1]],
    [/المملكة العربية السعودية/g, c.name[1]],
    [/نظام العمل السعودي/g, c.laborLaw[1]],
    [arWord("السعودية"), (_m, p) => p + c.short[1]],
    [arWord("المملكة"), (_m, p) => p + c.short[1]],
    [/حماية الأجور · مدد/g, c.wps.name[1]],
    [/ملف رواتب مدد/g, c.wps.file[1]],
    [arWord("مدد"), (_m, p) => p + "حماية الأجور"],
    [arWord("قوى"), (_m, p) => p + c.ministry.short[1]],
    [arWord("نطاقات"), (_m, p) => p + c.localisation.short[1]],
    [arWord("التوطين"), (_m, p) => p + c.localisation.short[1]],
    [/غير السعوديين/g, "الوافدين"],
    [/غير السعودي/g, "الوافد"],
    [/غير سعوديين/g, "وافدين"],
    [/غير سعودي/g, "وافد"],
    // سعودي، السعودي، سعوديًا، السعوديين، سعوديون، سعودية → the country's demonym with the same article and ending
    [new RegExp(`(?<![${AR}])([وبلفك]?)(ال)?سعودي(ًا|ين|ون|ة|ات)?(?![${AR}])`, "g"), (_m, p, al, end) => (p || "") + (al || "") + dem + (end || "")],
    [/التأمينات الاجتماعية/g, c.si.short[1]],
    [arWord("التأمينات"), (_m, p) => p + c.si.short[1]],
    [arWord("ساند"), (_m, p) => p + c.si.parts.saned[1]],
    [/الهوية الوطنية/g, c.nationalId.citizen[1]],
    [arWord("الإقامات"), (_m, p) => p + c.nationalId.resident[1]],
    [arWord("الإقامة"), (_m, p) => p + c.nationalId.resident[1]],
    [arWord("إقامة"), (_m, p) => p + c.nationalId.resident[1]],
    [/ر\.س/g, c.currency.symbol[1]],
    [/150%/g, ot],
  ];
}

const cache = new Map<string, Rule[]>();
const MEMO_MAX = 4000;
const memo = new Map<string, string>();

/** The text with Saudi-specific terms replaced by the country's own (unchanged for Saudi Arabia). */
export function localText(s: string, c: CountryInfo, lang: Lang): string {
  if (c.code === "SA" || !s) return s;
  const key = c.code + lang + s;
  const hit = memo.get(key);
  if (hit !== undefined) return hit;
  const rk = c.code + lang;
  let rules = cache.get(rk);
  if (!rules) { rules = lang === "ar" ? arRules(c) : enRules(c); cache.set(rk, rules); }
  let out = s;
  for (const [re, to] of rules) out = typeof to === "string" ? out.replace(re, to) : out.replace(re, to as (...m: string[]) => string);
  if (memo.size > MEMO_MAX) memo.clear();
  memo.set(key, out);
  return out;
}
